import { useNavigate } from "react-router-dom";
import { API_USERS } from "../../constants/apis";
import { COMPONENT, LOGIN, TEXT } from "../../constants/constants";
import { App } from "antd";
import { showConfetti } from "../../components/elements/ConfettiView";
import styled from "styled-components";
import {
  primary10,
  PRIMARY_COLOR,
  PRIMARY_LIGHT,
} from "../../constants/colors";
import {
  CheckmarkCircle,
  BriefcaseOutline,
  CardOutline,
} from "@raresail/react-ionicons";
import { alpha } from "@mui/material";

// ─── Role Selector ──────────────────────────────────────────────
const RoleSelector = ({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (role: string) => void;
  error?: string;
}) => {
  const roles = [
    {
      value: "investor",
      label: "Investor",
      description: "Fund loans & assets to earn attractive returns on your capital",
      Icon: BriefcaseOutline,
    },
    {
      value: "borrower",
      label: "Business Borrower",
      description: "Access financing for your business — loans, equipment & more",
      Icon: CardOutline,
    },
  ];

  return (
    <div>
      <RoleLabelRow>
        <span className="font13 textDark">I want to join as</span>
        <span className="red">*</span>
      </RoleLabelRow>

      <RoleGrid>
        {roles.map((role) => {
          const selected = value === role.value;
          return (
            <RoleCard
              key={role.value}
              selected={selected}
              onClick={() => onChange(role.value)}
              className="animate"
            >
              {/* Check indicator */}
              {selected && (
                <CheckBadge className="animate">
                  <CheckmarkCircle color={PRIMARY_COLOR} fontSize={18} />
                </CheckBadge>
              )}

              <IconCircle selected={selected} className="animate">
                <role.Icon fontSize={22} />
              </IconCircle>

              <RoleCardContent>
                <div className="font13 semiBold" style={{ lineHeight: 1.3 }}>
                  {role.label}
                </div>
                <RoleDesc className="font11">{role.description}</RoleDesc>
              </RoleCardContent>
            </RoleCard>
          );
        })}
      </RoleGrid>

      {error && <p className="font11 red" style={{ marginTop: 4 }}>{error}</p>}
    </div>
  );
};

const RoleLabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
`;

const RoleGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const RoleCard = styled.div<{ selected?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  text-align: center;
  /* Tighter top/bottom padding so cards are more compact */
  padding: 16px 12px 14px;
  border-radius: 12px;
  cursor: pointer;
  border: 1.5px solid
    ${({ selected }) => (selected ? PRIMARY_COLOR : PRIMARY_LIGHT)};
  background: ${({ selected }) =>
    selected ? alpha(PRIMARY_COLOR, 0.04) : "#fafafa"};
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  box-shadow: ${({ selected }) =>
    selected
      ? `0 0 0 3px ${alpha(PRIMARY_COLOR, 0.08)}`
      : "0 1px 3px rgba(0,0,0,0.04)"};

  &:hover {
    border-color: ${({ selected }) =>
      selected ? PRIMARY_COLOR : alpha(PRIMARY_COLOR, 0.4)};
    background: ${({ selected }) =>
      selected ? alpha(PRIMARY_COLOR, 0.04) : alpha(PRIMARY_COLOR, 0.02)};
    box-shadow: ${({ selected }) =>
      selected
        ? `0 0 0 3px ${alpha(PRIMARY_COLOR, 0.08)}`
        : `0 2px 8px rgba(0,0,0,0.07)`};
  }
`;

const RoleCardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const CheckBadge = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
`;

const IconCircle = styled.div<{ selected?: boolean }>`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ selected }) => (selected ? "#fff" : PRIMARY_COLOR)};
  background: ${({ selected }) => (selected ? PRIMARY_COLOR : primary10)};
  transition: all 0.2s ease;
  flex-shrink: 0;
`;

const RoleDesc = styled.div`
  color: #8a9bbf;
  line-height: 1.4;
  margin-top: 1px;
`;

// ─── Hook ────────────────────────────────────────────────────────
export const useRegisterForm = ({ form, updateFormValues, error }: any = {}) => {
  const navigate = useNavigate();
  const { notification } = App.useApp();

  // Password-match validation (evaluated fresh each render)
  const passwordsMatch =
    !form?.confirm_password ||
    form?.password === form?.confirm_password;

  // Phone must be exactly 9 digits
  const phoneRaw = (form?.phone_number || "").replace(/\D/g, "");
  const phoneValid = phoneRaw.length === 0 || phoneRaw.length === 9;

  const fields = [
    // ── Role selector (full width) ──
    {
      type: COMPONENT,
      fullWidth: true,
      component: (
        <RoleSelector
          value={form?.user_type}
          onChange={(role: string) =>
            updateFormValues && updateFormValues({ user_type: role })
          }
          error={error?.user_type}
        />
      ),
    },
    // Hidden validation-only field
    {
      field: "user_type",
      type: TEXT,
      visible: false,
      notValid: !form?.user_type
        ? "Please select whether you are an Investor or Business Borrower"
        : null,
    },

    // ── Name fields ──
    {
      field: "first_name",
      type: TEXT,
      required: true,
    },
    {
      field: "last_name",
      type: TEXT,
      required: true,
    },

    // ── Phone ──
    {
      field: "phone_number",
      label: "Phone Number",
      type: TEXT,
      required: true,
      notValid:
        form?.phone_number && !phoneValid
          ? "Enter 9 digits after the country code (e.g. 7XXXXXXXX)"
          : null,
      props: {
        prefix: "+256",
        maxLength: 9,
        inputMode: "numeric",
        placeholder: "7XXXXXXXX",
        helperText: "Enter your mobile money number (9 digits)",
      },
    },

    // ── Email ──
    {
      label: "Email Address",
      field: "email",
      type: TEXT,
      required: true,
      props: {
        type: "email",
        placeholder: "you@example.com",
      },
    },

    // ── Password ──
    {
      field: "password",
      type: TEXT,
      required: true,
      props: {
        type: "password",
        helperText: "Minimum 8 characters",
      },
    },
    {
      field: "confirm_password",
      label: "Confirm Password",
      type: TEXT,
      required: true,
      notValid: !passwordsMatch ? "Passwords do not match" : null,
      props: {
        type: "password",
      },
    },

    // ── Terms notice (full width) ──
    {
      type: COMPONENT,
      fullWidth: true,
      component: (
        <div
          className="font11 grey textCenter"
          style={{ lineHeight: 1.6, margin: "-2px 0" }}
        >
          By creating an account you agree to our{" "}
          <span className="main semiBold pointer">Terms of Service</span> and{" "}
          <span className="main semiBold pointer">Privacy Policy</span>
        </div>
      ),
    },
  ];

  const onSubmit = ({ payload }: any) => {
    // Clean phone → E.164 format
    const rawDigits = (payload.phone_number || "").replace(/\D/g, "");
    payload.phone_number = `+256${rawDigits}`;

    // Strip confirm_password — backend doesn't need it
    delete payload.confirm_password;

    const openNotification = () => {
      notification.success({
        message: "Account created!",
        description: (
          <div className="font12">
            We sent a 6-digit verification code to your phone. Enter it to
            activate your account.
          </div>
        ),
        duration: 6,
      });
    };

    const phoneE164 = payload.phone_number;
    const onSuccess = () => {
      navigate(`/auth/verify-phone?phone=${encodeURIComponent(phoneE164)}`);
      showConfetti();
      openNotification();
    };

    return {
      api: API_USERS,
      params: payload,
      onSuccess,
    };
  };

  return { fields, onSubmit, route: LOGIN };
};